import { motion } from "framer-motion";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { FiArrowDown } from "react-icons/fi";
import {
  animationVariants,
  motionProps,
  transitions,
} from "../../hooks/useOptimizedAnimation";

export default function Hero() {
  const { t } = useTranslation();

  const heroContent = {
    title: t("hero.title1"),
    subtitle: t("hero.subtitle1"),
    description: t("hero.description1"),
    image: "/images/image_1.jpg",
    cta: t("hero.cta1"),
  };

  return (
    <div className="relative md:h-screen  w-full overflow-hidden touch-pan-y ">
      {/* Mobile-Optimized Background */}

      {/* Hero Content */}
      <div
        className="relative flex lg:h-full w-full items-center bg-cover bg-center bg-no-repeat md:py-0 py-14"
        style={{
          backgroundImage: `linear-gradient(135deg, rgba(22, 101, 52, 0.85), rgba(21, 128, 61, 0.75), rgba(34, 197, 94, 0.3)), url(${heroContent.image})`,
          backgroundPosition: "center",
          backgroundSize: "cover",
        }}
      >
        {/* Content Overlay */}

        {/* Mobile-First Content Layout */}
        <div className="relative z-10 flex flex-col justify-center h-full px-4 py-8 sm:px-6 md:px-8 lg:px-16">
          <div className="max-w-4xl mx-auto text-center">
            <motion.div
              className="space-y-4 sm:space-y-6 md:space-y-8"
              variants={animationVariants.fadeInUp}
              initial="hidden"
              animate="visible"
              transition={{ ...transitions.medium, delay: 0.1 }}
              {...motionProps}
            >
              {/* Mobile-Optimized Badge */}
              <motion.div
                className="inline-flex items-center rounded-full bg-yellow-500/25 px-3 py-1.5 sm:px-4 sm:py-2 text-xs sm:text-sm font-medium text-yellow-300 backdrop-blur-sm border border-yellow-400/20"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
              >
                <span className="mr-1.5 h-1.5 w-1.5 sm:h-2 sm:w-2 rounded-full bg-yellow-400 animate-pulse"></span>
                Making a Difference
              </motion.div>

              {/* Mobile-Optimized Typography */}
              <div className="space-y-3 sm:space-y-4 md:space-y-6">
                <h1 className="text-2xl leading-tight font-bold sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl">
                  <span className="bg-gradient-to-r from-white via-yellow-100 to-yellow-300 bg-clip-text text-transparent">
                    {heroContent.title}
                  </span>
                </h1>
                <p className="text-base font-semibold text-yellow-300 sm:text-lg md:text-xl lg:text-2xl">
                  {heroContent.subtitle}
                </p>
                <p className="text-sm text-gray-100 leading-relaxed max-w-2xl mx-auto sm:text-base md:text-lg">
                  {heroContent.description}
                </p>
              </div>

              {/* Mobile-Optimized CTA Buttons */}
              <div className="flex flex-col gap-3 sm:flex-row sm:gap-4 justify-center pt-4 sm:pt-6">
                <motion.button
                  className="group relative overflow-hidden rounded-lg sm:rounded-xl bg-gradient-to-r from-yellow-500 to-yellow-400 px-6 py-3 sm:px-8 sm:py-4 text-sm sm:text-base md:text-lg font-bold text-green-900 shadow-xl transition-all duration-300 hover:scale-105 hover:shadow-yellow-500/25 active:scale-95"
                  whileHover={animationVariants.hover}
                  whileTap={animationVariants.tap}
                  {...motionProps}
                >
                  <span className="relative z-10 flex items-center justify-center">
                    {heroContent.cta}
                    <FiArrowDown className="ml-2 h-4 w-4 sm:h-5 sm:w-5 transition-transform group-hover:translate-x-1" />
                  </span>
                  <div className="absolute inset-0 bg-gradient-to-r from-yellow-400 to-yellow-300 opacity-0 transition-opacity group-hover:opacity-100"></div>
                </motion.button>

                <motion.button
                  className="rounded-lg sm:rounded-xl border-2 border-white/30 bg-white/10 px-6 py-3 sm:px-8 sm:py-4 text-sm sm:text-base md:text-lg font-semibold text-white backdrop-blur-sm transition-all duration-300 hover:bg-white/20 hover:border-white/50 active:scale-95"
                  whileHover={animationVariants.hover}
                  whileTap={animationVariants.tap}
                  {...motionProps}
                >
                  Learn More
                </motion.button>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
}
